import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { loginUser, registerUser, logoutUser, getMe } from './Index';
import api from './Axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading]       = useState(true);
  const [userToken, setUserToken]       = useState(null);
  const [userData, setUserData]         = useState(null);
  const [error, setError]               = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const tokenRefreshTimer               = useRef(null);

  // Hydrate auth state from localStorage on app start
  useEffect(() => {
    initializeAuth();
    return () => clearRefreshTimer();
  }, []);

  const initializeAuth = () => {
    try {
      const storedToken = localStorage.getItem('authToken');
      const storedUser  = localStorage.getItem('userData');

      if (storedToken && storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserToken(storedToken);
        setUserData(parsedUser);
        api.defaults.headers.common['x-auth-token'] = storedToken;
      }
    } catch {
      clearStoredAuth();
    } finally {
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  const persistAuth = (token, user) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('userData', JSON.stringify(user));
    api.defaults.headers.common['x-auth-token'] = token;
    setUserToken(token);
    setUserData(user);
  };

  const clearStoredAuth = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userEmail');
    delete api.defaults.headers.common['x-auth-token'];
    delete api.defaults.headers.common['Authorization'];
    setUserToken(null);
    setUserData(null);
    clearRefreshTimer();
  };

  const clearRefreshTimer = () => {
    if (tokenRefreshTimer.current) {
      clearTimeout(tokenRefreshTimer.current);
      tokenRefreshTimer.current = null;
    }
  };

  // ─── Auth Actions ───────────────────────────────────────────

  const register = async (name, email, phone, password) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await registerUser(name, email, phone, password);
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data } = await loginUser(email, password);
      const { token, user } = data;

      persistAuth(token, user);

      if (rememberMe) {
        localStorage.setItem('userEmail', email);
      } else {
        localStorage.removeItem('userEmail');
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      if (userToken) {
        await logoutUser().catch(() => {
          // Server-side logout failure is non-critical; proceed with local cleanup
        });
      }
    } finally {
      clearStoredAuth();
      setIsLoading(false);
      window.location.href = '/';
    }
  };

  const refreshUserProfile = async () => {
    try {
      const { data } = await getMe();
      const updatedUser = data.data || data;
      setUserData(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      return updatedUser;
    } catch (err) {
      if (err.message?.includes('401') || err.response?.status === 401) {
        await logout();
      }
      throw err;
    }
  };

  // ─── Helpers ────────────────────────────────────────────────

  const isAuthenticated = () => !!userToken && !!userData;

  const getUserId = () => userData?._id || userData?.id || null;

  // SessionManager: saved email for "remember me"
  const SessionManager = {
    loadSavedEmail: () => {
      try {
        const savedEmail = localStorage.getItem('userEmail');
        return { email: savedEmail || '', remember: !!savedEmail };
      } catch {
        return { email: '', remember: false };
      }
    },
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        userToken,
        userData,
        error,
        isInitialized,
        isAuthenticated,
        getUserId,
        login,
        logout,
        register,
        refreshUserProfile,
        SessionManager,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for cleaner imports in pages
export const useAuth = () => useContext(AuthContext);

export default AuthContext;